import { useInfiniteQuery } from '@tanstack/react-query';

import { fetchLADClaimFiles, type GetLADClaimFilesRequestParams } from '../data-provider/lad-claim-files/api';

export interface UseGetLADClaimFilesParams {
  ClaimKey: number;
  pageSize?: number;
  sortColumn?: string;
  sortDirection?: 'ASC' | 'DESC';
}

export const useGetLADClaimFiles = ({
  ClaimKey,
  pageSize = 10,
  sortColumn = 'sequence',
  sortDirection = 'DESC',
}: UseGetLADClaimFilesParams) => {
  return useInfiniteQuery({
    enabled: Boolean(ClaimKey),
    getNextPageParam: (lastPage: any, allPages) => {
      const totalRows = lastPage?.totalRows ?? 0;
      const loadedRows = allPages.reduce((sum, page) => {
        const pageCount = Array.isArray(page?.data) ? page.data.length : 0;
        return sum + pageCount;
      }, 0);
      return loadedRows < totalRows ? allPages.length + 1 : undefined;
    },
    initialPageParam: 1,
    queryFn: ({ pageParam = 1 }) => {
      const params: GetLADClaimFilesRequestParams = {
        claimKey: Number(ClaimKey),
        pageIndex: Number(pageParam),
        pageSize,
        sortColumn,
        sortDirection,
      };
      return fetchLADClaimFiles(params);
    },
    queryKey: ['claims-loss-and-damage-files', ClaimKey, sortColumn, sortDirection],
    select: (data) => {
      const flatFiles = data.pages.flatMap((page) => page?.data ?? []);
      const lastPage = data.pages[data.pages.length - 1];
      const totalRows = Number(lastPage?.totalRows ?? 0);
      const pageIndex = data.pages.length;
      const hasMore = flatFiles.length < totalRows;
      return {
        ...data,
        files: flatFiles,
        flatFiles,
        hasMore,
        nextPage: hasMore ? pageIndex + 1 : undefined,
        pageIndex,
        totalRows,
      };
    },
  });
};
